<div class="plans-container">
  <!-- Header -->
  <div class="section-header">
    <div class="header-content">
      <h2>Subscription Plans</h2>
      <p class="section-description">Manage SaaS subscription tiers and pricing</p>
    </div>
    <button class="btn btn-primary" (click)="startCreate()" [disabled]="isCreating || isSaving">
      <i class="fas fa-plus"></i> Add Plan
    </button>
  </div>

  <!-- Messages -->
  <div class="message error-message" *ngIf="errorMessage">
    <i class="fas fa-exclamation-circle"></i> {{ errorMessage }}
  </div>
  <div class="message success-message" *ngIf="successMessage">
    <i class="fas fa-check-circle"></i> {{ successMessage }}
  </div>

  <!-- Loading State -->
  <div class="loading-container" *ngIf="isLoading">
    <i class="fas fa-spinner fa-spin"></i> Loading plans...
  </div>

  <!-- Create Form -->
  <div class="create-form card" *ngIf="isCreating">
    <h3>New Plan</h3>
    <div class="form-grid">
      <div class="form-group">
        <label>Name *</label>
        <input type="text" [(ngModel)]="createForm.name" placeholder="Plan name" />
      </div>
      <div class="form-group">
        <label>Level</label>
        <input type="number" [(ngModel)]="createForm.level" min="0" />
        <small class="help-text">Higher level = more features</small>
      </div>
      <div class="form-group full-width">
        <label>Description</label>
        <textarea [(ngModel)]="createForm.description" placeholder="Plan description" rows="2"></textarea>
      </div>
      <div class="form-group">
        <label>Monthly Price</label>
        <div class="input-with-prefix">
          <span class="prefix">$</span>
          <input type="number" [(ngModel)]="createForm.monthlyPrice" min="0" step="0.01" />
        </div>
      </div>
      <div class="form-group">
        <label>Annual Price</label>
        <div class="input-with-prefix">
          <span class="prefix">$</span>
          <input type="number" [(ngModel)]="createForm.annualPrice" min="0" step="0.01" />
        </div>
      </div>
      <div class="form-group">
        <label>Status</label>
        <select [(ngModel)]="createForm.status">
          <option value="Active">Active</option>
          <option value="Pending">Pending</option>
          <option value="Disabled">Disabled</option>
        </select>
      </div>
    </div>
    <div class="form-actions">
      <button class="btn btn-secondary" (click)="cancelCreate()" [disabled]="isSaving">Cancel</button>
      <button class="btn btn-primary" (click)="saveCreate()" [disabled]="isSaving">
        <i class="fas fa-spinner fa-spin" *ngIf="isSaving"></i>
        {{ isSaving ? 'Saving...' : 'Create Plan' }}
      </button>
    </div>
  </div>

  <!-- Plans Grid -->
  <div class="plans-grid" *ngIf="!isLoading">
    <div class="plan-card card" *ngFor="let pd of planDisplays" [class.editing]="pd.isEditing">
      <!-- View Mode -->
      <ng-container *ngIf="!pd.isEditing">
        <div class="plan-header">
          <div class="plan-level">Level {{ pd.plan.Level }}</div>
          <span class="status-badge" [ngClass]="getStatusClass(pd.plan.Status)">
            {{ pd.plan.Status }}
          </span>
        </div>
        <h3 class="plan-name">{{ pd.plan.Name }}</h3>
        <p class="plan-description">{{ pd.plan.Description || 'No description' }}</p>
        <div class="plan-pricing">
          <div class="price-item">
            <span class="price-label">Monthly</span>
            <span class="price-value">{{ formatCurrency(pd.plan.MonthlyPrice || 0) }}</span>
          </div>
          <div class="price-item">
            <span class="price-label">Annual</span>
            <span class="price-value">{{ formatCurrency(pd.plan.AnnualPrice || 0) }}</span>
          </div>
        </div>
        <div class="plan-actions">
          <button class="btn btn-secondary" (click)="startEdit(pd)" [disabled]="isSaving">
            <i class="fas fa-edit"></i> Edit
          </button>
          <button class="btn btn-icon btn-danger" title="Delete" (click)="confirmDelete(pd)" [disabled]="isSaving">
            <i class="fas fa-trash"></i>
          </button>
        </div>
      </ng-container>

      <!-- Edit Mode -->
      <ng-container *ngIf="pd.isEditing">
        <div class="edit-form">
          <div class="form-group">
            <label>Name</label>
            <input type="text" [(ngModel)]="pd.editForm.name" />
          </div>
          <div class="form-group">
            <label>Level</label>
            <input type="number" [(ngModel)]="pd.editForm.level" min="0" />
          </div>
          <div class="form-group">
            <label>Description</label>
            <textarea [(ngModel)]="pd.editForm.description" rows="2"></textarea>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>Monthly</label>
              <div class="input-with-prefix">
                <span class="prefix">$</span>
                <input type="number" [(ngModel)]="pd.editForm.monthlyPrice" min="0" step="0.01" />
              </div>
            </div>
            <div class="form-group">
              <label>Annual</label>
              <div class="input-with-prefix">
                <span class="prefix">$</span>
                <input type="number" [(ngModel)]="pd.editForm.annualPrice" min="0" step="0.01" />
              </div>
            </div>
          </div>
          <div class="form-group">
            <label>Status</label>
            <select [(ngModel)]="pd.editForm.status">
              <option value="Active">Active</option>
              <option value="Pending">Pending</option>
              <option value="Disabled">Disabled</option>
            </select>
          </div>
          <div class="form-actions">
            <button class="btn btn-secondary" (click)="cancelEdit(pd)" [disabled]="isSaving">Cancel</button>
            <button class="btn btn-primary" (click)="saveEdit(pd)" [disabled]="isSaving">
              <i class="fas fa-spinner fa-spin" *ngIf="isSaving"></i>
              Save
            </button>
          </div>
        </div>
      </ng-container>
    </div>

    <!-- Empty State -->
    <div class="empty-state card" *ngIf="planDisplays.length === 0 && !isCreating">
      <i class="fas fa-layer-group"></i>
      <p>No plans defined</p>
      <button class="btn btn-primary" (click)="startCreate()">Create Your First Plan</button>
    </div>
  </div>

  <!-- Confirm Dialog -->
  <div class="modal-overlay" *ngIf="confirmDialogVisible" (click)="closeConfirmDialog()">
    <div class="modal-dialog" (click)="$event.stopPropagation()">
      <h3>{{ confirmDialogTitle }}</h3>
      <p>{{ confirmDialogMessage }}</p>
      <div class="modal-actions">
        <button class="btn btn-secondary" (click)="closeConfirmDialog()">Cancel</button>
        <button class="btn btn-danger" (click)="confirmDialogYes()" [disabled]="isSaving">
          <i class="fas fa-spinner fa-spin" *ngIf="isSaving"></i>
          Delete
        </button>
      </div>
    </div>
  </div>
</div>
